import React, { useState } from 'react';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Button from '../../Shared/UI/Button/components/Button';

import {
  useAdminEnrollmentManagementState,
  useAdminEnrollmentManagementActions,
} from './AdminEnrollmentManagementContext';

import {
  enrollFree,
  enrollPremiumFeeWaived,
  upgradeToPremiumFeeWaived,
  downgradeToFree,
} from './restApi';

const enrollmentStatusLabels = {
  not_enrolled: 'Not Enrolled',
  free: 'Free',
  premium: 'Premium',
};

const subscriptionTypeLabels = {
  none: 'None',
  fee_waived: 'Fee Waived',
  stripe_paid: 'Stripe Paid',
  group_paid: 'Group Paid',
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const SORTABLE_FIELDS = {
  name: (user) => user.name?.toLowerCase() || '',
  enrollmentStatus: (user) => user.enrollmentStatus || '',
};

const sortUsers = (users, sortField, sortDirection) => {
  if (!sortField) return users;

  const getValue = SORTABLE_FIELDS[sortField];
  const sorted = [...users].sort((a, b) => {
    const valueA = getValue(a);
    const valueB = getValue(b);

    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  });

  return sortDirection === 'desc' ? sorted.reverse() : sorted;
};

const AdminEnrollmentManagementTable = () => {
  const state = useAdminEnrollmentManagementState();
  const { onResponse, onError } = useAdminEnrollmentManagementActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content');

  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const users = state?.users || [];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = sortUsers(users, sortField, sortDirection);

  const handleEnrollFree = (user) => {
    if (!window.confirm(`Are you sure you want to enroll ${user.name} in the free tier?`)) return;

    enrollFree({
      onResponse,
      onError,
      body: { authenticityToken, userId: user.id },
    });
  };

  const handleEnrollPremiumFeeWaived = (user) => {
    if (!window.confirm(`Are you sure you want to enroll ${user.name} in premium (fee waived)?`)) return;

    enrollPremiumFeeWaived({
      onResponse,
      onError,
      body: { authenticityToken, userId: user.id },
    });
  };

  const handleUpgradeToPremiumFeeWaived = (user) => {
    if (!window.confirm(`Are you sure you want to upgrade ${user.name} to premium (fee waived)?`)) return;

    upgradeToPremiumFeeWaived({
      onResponse,
      onError,
      body: { authenticityToken, userId: user.id },
    });
  };

  const handleDowngradeToFree = (user) => {
    if (!window.confirm(`Are you sure you want to downgrade ${user.name} to the free tier?`)) return;

    downgradeToFree({
      onResponse,
      onError,
      body: { authenticityToken, userId: user.id },
    });
  };

  const renderActions = (user) => {
    if (user.enrollmentStatus === 'not_enrolled') {
      return (
        <>
          <Button onClick={() => handleEnrollFree(user)}>
            Enroll Free
          </Button>
          <Button onClick={() => handleEnrollPremiumFeeWaived(user)}>
            Enroll Premium (Fee Waived)
          </Button>
        </>
      );
    }

    if (user.enrollmentStatus === 'free') {
      return (
        <Button onClick={() => handleUpgradeToPremiumFeeWaived(user)}>
          Upgrade to Premium (Fee Waived)
        </Button>
      );
    }

    if (user.enrollmentStatus === 'premium' && user.subscriptionType === 'fee_waived') {
      return (
        <Button onClick={() => handleDowngradeToFree(user)}>
          Downgrade to Free
        </Button>
      );
    }

    return null;
  };

  if (users.length === 0) {
    return (
      <p style={{ textAlign: 'center', fontWeight: 'bold' }}>No users available</p>
    );
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
            Name
          </Th>
          <Th>Email</Th>
          <Th onClick={() => handleSort('enrollmentStatus')} style={{ cursor: 'pointer' }}>
            Enrollment Status
          </Th>
          <Th>Tier</Th>
          <Th>Subscription Type</Th>
          <Th>Enrolled At</Th>
          <Th />
        </Tr>
      </Thead>
      <Tbody>
        {sortedUsers.map((user) => (
          <Tr key={user.id}>
            <Td>{user.name}</Td>
            <Td>{user.email}</Td>
            <Td>{enrollmentStatusLabels[user.enrollmentStatus] || user.enrollmentStatus}</Td>
            <Td>{user.tier || 'N/A'}</Td>
            <Td>{subscriptionTypeLabels[user.subscriptionType] || user.subscriptionType}</Td>
            <Td>{formatDate(user.enrolledAt)}</Td>
            <Td>{renderActions(user)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

export default AdminEnrollmentManagementTable;
